import { rem } from 'polished'
import { useCallback, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'

import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import LineArrowDownIcon from 'LuxKit/Icons/line/LineArrowDownIcon'
import type { AccommodationRoomTileActionsState } from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationRoomTileActions from 'components/AccommodationKit/AccommodationRoomTileActions'
import AccommodationSameRoomTileImage from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileImage'
import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import Pane from 'components/Common/Pane'
import ScrollAnchor from 'components/Common/ScrollAnchor'
import type { OfferPageFilterType, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import useToggle from 'hooks/useToggle'
import { take } from 'lib/array/arrayUtils'
import { hasSelectedRooms, preventRenderWithOpenFilter } from 'lib/offer/offerPageUtil'
import uuidV4 from 'lib/string/uuidV4Utils'
import {
  arePropertyRatesLoading,
  getPropertyRatesKey,
  isPropertyAvailableForPurchase,
} from 'selectors/offerPage/offerPageSelectors'

import PropertyRateOption from './PropertyRateOption'
import PropertyRateOptionMerged from './PropertyRateOptionMerged'
import useGroupedPropertyRatesByRefundability from './useGroupedPropertyRatesByRefundability'
import AccommodationSameRoomTile from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTile'
import AccommodationSameRoomTileDetails from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileDetails'
import { AccommodationSameRoomTileOptions } from 'components/AccommodationKit/AccommodationSameRoom/AccommodationSameRoomTileOptions'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import CardCarousel from 'LuxKit/Carousel/CardCarousel'
import Group from 'components/utils/Group'
import PropertyAnytimeRateOption from './PropertyAnytimeRateOption'
import MessageBanner from 'LuxKit/Banners/MessageBanner'

const ShowMoreContent = styled(Pane)`
  border-top: 0 !important;
`

const MAX_RATES_TO_SHOW = 100

interface Props {
  property: App.Property.Property
  offer: App.HotelOffer | undefined
  propertyRoom: App.Property.Room
  availableRooms: number | undefined
  rates: Array<App.PropertyRate>
  roomFitsCapacity: boolean
  datesAvailable: boolean
  checkIn?: string
  rooms: Array<OfferPageRoom>
  isSelected: boolean
  isHighlighted?: boolean
  roomRateUnmatched?: boolean
  onViewPackageOptions: () => void
  airport?: App.Airport
  roomListId: string
  roomPosition: number
  availableForPurchase?: boolean
  areAnytimeRates: boolean
  isLoading: boolean
}

function PropertyRoomOption(props: Props) {
  const {
    property,
    offer,
    propertyRoom,
    availableRooms = 0,
    rates,
    roomFitsCapacity,
    datesAvailable,
    checkIn,
    rooms,
    isSelected,
    isHighlighted = false,
    roomRateUnmatched = true,
    onViewPackageOptions,
    roomListId,
    roomPosition,
    availableForPurchase,
    areAnytimeRates,
    isLoading,
  } = props

  const intl = useIntl()

  /************************************************************************************************/
  /* STOP! Do not add any more hooks for deriving packages/rooms or options/rates to this         */
  /* component! This logic is being refactored out into selectors! If you _need_ to add new       */
  /* logic, speak to Xiao.                                                                        */
  /************************************************************************************************/

  // NB: `0`s become `undefined` so we can easily conditionally render `<BookingsCountLabel/>` later.
  const bookingsCount = propertyRoom.stats.booked || undefined
  const [showingMoreRates, showMoreRates] = useToggle()
  const offerPageDispatch = useOfferPageDispatch()
  const promoteRoom = isHighlighted || isSelected
  const promoteRoomWithSpecificRate = !isSelected && isHighlighted && !roomRateUnmatched
  const promoteRoomWithSomeRates = (isSelected || (isHighlighted && roomRateUnmatched)) && !!rates.length
  const promoteRoomWithoutRates = (isSelected || (isHighlighted && roomRateUnmatched)) && !rates.length
  const initialRatesToShow = promoteRoomWithSpecificRate ? 1 : 3
  const roomCount = rooms.length

  useEffect(() => {
    if (isHighlighted) {
      onViewPackageOptions()
    }
  }, [isHighlighted]) // oxlint-disable-line react-hooks/exhaustive-deps

  const actionsState = useMemo<AccommodationRoomTileActionsState | undefined>(() => {
    if (isLoading) return undefined
    if (!availableForPurchase) return 'sold-out'

    if (availableForPurchase) {
      const anytimeRatesActionState = areAnytimeRates ? 'available-on-dates-variant' : 'available-on-dates'
      if (!checkIn) return anytimeRatesActionState
      if (!roomFitsCapacity && datesAvailable) return 'exceeded-capacity'
      if (!datesAvailable) return 'sold-out-on-dates-or-exceeded-capacity'
    }
  }, [areAnytimeRates, availableForPurchase, checkIn, datesAvailable, isLoading, roomFitsCapacity])

  const toggleFilter = useCallback((filter: OfferPageFilterType) => {
    offerPageDispatch({ type: OfferPageActions.setPropertyRoomId, roomId: propertyRoom.id })
    offerPageDispatch({ type: OfferPageActions.toggleFilter, filter })
  }, [offerPageDispatch, propertyRoom.id])

  const handleChangeDatesClick = useCallback(() => {
    toggleFilter('checkin')
  }, [toggleFilter])

  const handleCheckAvailabilityClick = useCallback(() => {
    // If dates aren't set, the CTA will be "Select dates", so we'll use the default occupancy and
    // skip directly to selecting dates.
    toggleFilter((areAnytimeRates || hasSelectedRooms(rooms)) ? 'checkin' : 'capacity')
  }, [areAnytimeRates, rooms, toggleFilter])

  const onChangeOccupancyClick = useCallback(() => {
    toggleFilter('capacity')
  }, [toggleFilter])

  const rateListId = useMemo(() => uuidV4(),
  // oxlint-disable-next-line react-hooks/exhaustive-deps
    [rates]) // regenerate id if packages change

  // Group property rates by refundability
  const groupedPropertyRates = useGroupedPropertyRatesByRefundability(rates)

  const interestedInOptionText = intl.formatMessage({
    id: 'G-9nAaAqjseUDghjg-_K',
    defaultMessage: 'You were interested in this option.',
    description: 'Banner message when user previously viewed this specific rate option',
  })
  const interestedInRoomBookNowText = intl.formatMessage({
    id: 'aVl6CMk5kGcBFhZbG6_F',
    defaultMessage: 'You were interested in this room. Book now to secure your room!',
    description: 'Banner message encouraging user to book a previously viewed room',
  })
  const interestedInRoomText = intl.formatMessage({
    id: 'Dx8bErFB-i5-65LF4eFg',
    defaultMessage: 'You were interested in this room.',
    description: 'Banner message when user previously viewed this room',
  })
  const showMoreOptionsText = intl.formatMessage({
    id: 'k-g_5h0_vEmMsFKzMOYE',
    defaultMessage: 'Show more options',
    description: 'Button text to show more rate options in property room',
  })
  const showLessOptionsText = intl.formatMessage({
    id: 'BALA4ECaVVusLe3tdWN3',
    defaultMessage: 'Show less options',
    description: 'Button text to show fewer rate options in property room',
  })

  const carouselWidth = rates.length > 1 ? rem(300) : '100%'

  return (
    <Pane data-testid="package-option">
      <ScrollAnchor id={propertyRoom.id} vOffset={rem(80)} />

      {/* Selection indicator for multi-room */}
      {promoteRoom && (
        <MessageBanner kind="promotional-primary" icon={<LineArrowDownIcon />}>
          {promoteRoomWithSpecificRate && <b>{interestedInOptionText}</b>}
          {promoteRoomWithSomeRates && <b>{interestedInRoomBookNowText}</b>}
          {promoteRoomWithoutRates && <b>{interestedInRoomText}</b>}
        </MessageBanner>
      )}

      <AccommodationSameRoomTile roomPackage={propertyRoom}>
        <AccommodationSameRoomTileImage roomPackage={propertyRoom} />
        <AccommodationSameRoomTileDetails
          roomPackage={propertyRoom}
          roomCount={roomCount}
          urgencyLabels={<>
            {bookingsCount && <BookingsCountLabel bookings={bookingsCount} threshold="room" />}
            {checkIn && <RoomsRemainingLabel roomCount={availableRooms} threshold="rate" />}
          </>}
        >
          <AccommodationRoomTileActions
            roomPackage={propertyRoom}
            state={actionsState}
            isWalled={false}
            shouldShowAmenitiesAndPoliciesCta={false}
            onChangeOccupancyClick={onChangeOccupancyClick}
            onChangeDatesClick={handleChangeDatesClick}
            onCheckAvailabilityClick={handleCheckAvailabilityClick}
          />
        </AccommodationSameRoomTileDetails>

        <AccommodationSameRoomTileOptions>
          {propertyRoom && areAnytimeRates && (
            <PropertyAnytimeRateOption propertyRoom={propertyRoom} />
          )}

          {/* Package carousel */}
          <CSSBreakpoint max="mobile" dynamic>
            <CardCarousel
              snap="start"
              width={carouselWidth}
              gap={12}
              gutterSize={20}
            >
              {take(rates, MAX_RATES_TO_SHOW).map((rate, index) => (
                <PropertyRateOption
                  key={rate.rateId}
                  lpcOffer={offer}
                  rate={rate}
                  optionNumber={index + 1}
                  showOptionLabel={rates.length > 1}
                  roomListId={roomListId}
                  roomPosition={roomPosition}
                  rateListId={rateListId}
                />
              ))}
            </CardCarousel>
          </CSSBreakpoint>

          <CSSBreakpoint min="tablet" dynamic>
            {/* Property rates grouped by board code and inclusions */}
            {take(
              groupedPropertyRates || [],
              showingMoreRates ? MAX_RATES_TO_SHOW : initialRatesToShow,
            ).map((group, index) => {
              return (
                <PropertyRateOptionMerged
                  key={group.groupingKey}
                  lpcOffer={offer}
                  rates={group.rates}
                  optionNumber={index + 1}
                  showOptionLabel={groupedPropertyRates.length > 1}
                  roomListId={roomListId}
                  roomPosition={roomPosition}
                  rateListId={rateListId}
                  propertyTimezone={property.location.timezone}
                />
              )
            })}
            {groupedPropertyRates.length > initialRatesToShow && (
              <ShowMoreContent padding={12} border="neutral-five">
                <Group direction="horizontal" horizontalAlign="center">
                  <SmallToggleButton
                    on={showingMoreRates}
                    onClick={showMoreRates}
                    size="medium"
                  >
                    {showingMoreRates ? showLessOptionsText : showMoreOptionsText}
                  </SmallToggleButton>
                </Group>
              </ShowMoreContent>
            )}
          </CSSBreakpoint>

          {isLoading && <LoadingIndicator inline />}
        </AccommodationSameRoomTileOptions>
      </AccommodationSameRoomTile>
    </Pane>
  )
}

export default withOfferPageAndReduxState((state, appState) => ({
  rooms: state.rooms,
  checkIn: state.checkInDate,
  airport: state.airport,
  availableForPurchase: isPropertyAvailableForPurchase(state, appState),
  areAnytimeRates: !getPropertyRatesKey(state, appState),
  isLoading: arePropertyRatesLoading(state, appState),
  open: !!state.activeFilter,
}), PropertyRoomOption, preventRenderWithOpenFilter)
